
    // IMPORTS
    import { defineComponent, computed } from 'vue'
    import { useSparePartFilterStore } from '@/store/sparePartFilter'

    import { Chip, ChipList } from '@progress/kendo-vue-buttons'

    import { DbConstants } from '@/const'
    import { buildClassificationLocalization, buildLocalization, StringUtil } from '@/util'

    interface IFilterChip {
        filterType: 'type' | 'function' | 'text' | 'reset',
        filterValue: string,
        text: string,
        removable: boolean,
        onClick?: () => void
    }

    export default defineComponent({
        name: 'sparepart-filter-state',

        components: {
            Chip,
            ChipList
        },

        setup() {
            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const sparePartFilterStore = useSparePartFilterStore()
            const { localizePropValue } = buildClassificationLocalization()

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const publicData = {}

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const selectedFunctions = computed(() => sparePartFilterStore.selectedFunctions)
            const searchText = computed(() => sparePartFilterStore.searchState.text)
            const functionStats = computed(() => sparePartFilterStore.sparePartFunctionStats)
            const filterIsActive = computed(() => sparePartFilterStore.filterIsActive)

            const allFilterChips = computed(() => getAllFilterChips())

            const computedData = { filterIsActive, allFilterChips }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            const { localize } = buildLocalization()

            /** Event handler for removing a chip */
            function onRemove(chip: IFilterChip) {
                switch (chip.filterType) {
                    case 'type':
                        sparePartFilterStore.resetTypeTreeState()
                        break

                    case 'function':
                        sparePartFilterStore.removeSelectedFunction(chip.filterValue)
                        break

                    case 'text':
                        sparePartFilterStore.resetSearchState()
                        break
                }
            }

            /** Returns a css class to assign to the specified chip */
            function getClassName(chip: IFilterChip): string {
                switch (chip.filterType) {
                    case 'type':
                        return chip.filterValue == 'all'
                            ? 'filter-type-all'
                            : 'filter-type-restricted'

                    case 'function':
                        return 'filter-type-function'

                    case 'text':
                        return 'filter-type-text'

                    case 'reset':
                        return 'filter-type-reset'
                }
            }

            const publicMethods = { localize, onRemove, getClassName }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function localizeSparepartType(pv: string): string {
                return localizePropValue(DbConstants.sparePartPropertyNames.sparePartType, pv)
            }

            function localizeSparepartFunction(pv: string): string {
                return removeCurlyBraces(localizePropValue(DbConstants.sparePartPropertyNames.sparePartFunction, pv))
            }

            function removeCurlyBraces(s: string) {
                return s.replace(/\{[^{}]*\}/g, '')
            }

            function selectedFunctionChip(functionVal: string): IFilterChip {
                const resultCount = functionStats.value.get(functionVal)

                return {
                    filterType: 'function',
                    filterValue: functionVal,
                    text: chipText(null, localizeSparepartFunction(functionVal), resultCount),
                    removable: true
                }
            }

            function selectedTypeChip(): IFilterChip {
                const selectedType = sparePartFilterStore.typeTreeState.selected.ids[0]
                const text = selectedType == 'all'
                    ? localize('all')
                    : localizeSparepartType(selectedType)

                return {
                    filterType: 'type',
                    filterValue: selectedType,
                    text: chipText(localize('type'), text),
                    removable: selectedType != 'all'
                }
            }

            function searchTextChip(): IFilterChip {
                const resultCount = sparePartFilterStore.sparePartsFilteredBySearchText.length

                const text = `"${searchText.value}"`

                return {
                    filterType: 'text',
                    filterValue: '',
                    text: chipText(localize('search'), text, resultCount),
                    removable: true
                }
            }

            function chipText(label: string | null, value: string, resultCount?: number) {
                let text = label != null
                    ? `${label}: ${value}`
                    : value

                if (resultCount != null) {
                    text += ` [${resultCount}]`
                }

                return text
            }

            function resetFilterChip(): IFilterChip {
                return {
                    filterType: 'reset',
                    filterValue: '',
                    text: localize('reset'),
                    removable: false,
                    onClick: () => sparePartFilterStore.resetFilterState()
                }
            }

            /** Returns ALL chips according to the active filters */
            function getAllFilterChips(): IFilterChip[] {
                const filterChips: IFilterChip[] = []

                // Selected type
                filterChips.push(selectedTypeChip())

                // Search text
                if (StringUtil.hasContent(searchText.value)) {
                    filterChips.push(searchTextChip())
                }

                // Selected functions
                filterChips.push(...selectedFunctions.value.map(func => selectedFunctionChip(func)))

                // Reset filter button
                if (sparePartFilterStore.filterIsActive) {
                    filterChips.push(resetFilterChip())
                }

                return filterChips
            }

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
